import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import {
  Input,
  FormField,
  PasswordInput,
  SubmitButton,
  SubmittingHint,
  useSubmittingMutation,
} from "@/design-system/components/ui";
import { emailFieldError, isValidEmail } from "@/lib/emailValidator";
import { PortalShell } from "../components/PortalShell";
import { AccountDisabledPage } from "../components/AccountDisabledPage";
import { portalAuthApi } from "../api/auth";
import { PortalApiError } from "../api/client";
import { usePortalAuth } from "../PortalAuthContext";
import { dashboardPathFor } from "./RecipientDashboardPage";
import { safePortalReturnTo } from "../lib/returnTo";

// External Intake & Collection v1.0 PR 9b §8 — portal login.
// Email + password against /api/portal/auth/login.
//
// Portal Round-3 #3 — MFA was removed; valid credentials resolve
// straight to a signed-in session.
//
// Portal Round-3 #8 — the new-assignments and reminder emails append
// `?email=` so the login form lands pre-filled; read it on mount.
//
// Review loop (Q2 / A17) — `?returnTo=` brings a visitor back to the portal
// page that sent them here (an item link opened while signed out), both after
// signing in and when they turn out to be signed in already. Only a validated
// same-origin "/portal/…" path is honoured (safePortalReturnTo); anything else
// falls back to the dashboard.

export function BrokerLoginPage() {
  const auth = usePortalAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const returnTo = safePortalReturnTo(searchParams.get("returnTo"));
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Disable / Re-enable / Archive — set when login is rejected with a 403
  // disabled response; renders the full-page disabled message instead of the
  // generic inline "invalid credentials" error.
  const [disabledMessage, setDisabledMessage] = useState<string | null>(null);
  const { submit, pending } = useSubmittingMutation(
    () => portalAuthApi.login(email.trim().toLowerCase(), password, rememberMe),
    {
      onSuccess: (result) => {
        if (!result.me) {
          setError("Sign-in failed.");
          return;
        }
        auth.setMe(result.me);
        navigate(returnTo ?? dashboardPathFor(result.me.accountType, result.me.kind), {
          replace: true,
        });
      },
      onError: (err) => {
        // Disable / Re-enable / Archive — a 403 means the account is disabled;
        // show the full-page message (the server inlines the support contact).
        if (err instanceof PortalApiError && err.status === 403) {
          setDisabledMessage(err.message);
          return;
        }
        const msg = err instanceof PortalApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Sign-in failed.";
        setError(msg);
      },
    },
  );

  if (!auth.loading && auth.me) {
    return <Navigate to={returnTo ?? dashboardPathFor(auth.me.accountType, auth.me.kind)} replace />;
  }

  if (disabledMessage) {
    return <AccountDisabledPage message={disabledMessage} />;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!email.trim() || !password) {
      setError("Email and password are required.");
      return;
    }
    if (!isValidEmail(email)) {
      setError("Enter a valid email address.");
      return;
    }
    void submit();
  }

  return (
    <PortalShell pageTitle="Sign-in">
      <h1 className="text-h2 font-display text-onyx m-0">Sign in</h1>
      <p className="text-small text-gray-mid mt-space-2">
        Use the email and password for your Phoenix Capital portal account.
      </p>

      <form onSubmit={handleSubmit} className="mt-space-5 flex flex-col gap-space-4" noValidate>
        <FormField
          label="Email"
          required
          error={email ? emailFieldError(email, { required: true }) : undefined}
        >
          <Input
            type="email"
            inputMode="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={pending}
            invalid={!!email && !isValidEmail(email)}
            data-testid="broker-login-email"
          />
        </FormField>
        <FormField label="Password" required>
          <PasswordInput
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={pending}
            data-testid="broker-login-password"
          />
        </FormField>

        <label className="flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            disabled={pending}
            data-testid="broker-login-remember"
          />
          Keep me signed in on this device
        </label>

        {error && <p className="text-small text-status-error m-0" role="alert">{error}</p>}

        <SubmitButton type="submit" pending={pending} pendingLabel="Signing in…" data-testid="broker-login-submit">
          Sign in
        </SubmitButton>
        <SubmittingHint pending={pending} />
      </form>

      <p className="text-caption text-gray-mid mt-space-4">
        <Link to="/portal/password-reset" className="underline">Forgot your password?</Link>
        {" · "}
        <Link to="/portal/apply" className="underline">Apply to be a broker</Link>
      </p>
    </PortalShell>
  );
}
